import { t } from '@reos/i18n';
import type { McpApprovalItem } from './mcp-approvals-api.js';

/**
 * Krok UXK3 bod 2 — "misto tabulky ID/Nástroj/Stav karty lidskou větou"
 * (predloha: „Asistentka chce založit zakázku Lipová 12"). `args` jsou
 * syrovy JSON tool callu (`mcp_approvals.args`, viz `reos-tools.ts`
 * inputSchema) — veta se sklada jen z poli, ktera dany nastroj skutecne ma.
 */

const LOCALE = 'cs' as const;

function argText(args: unknown, key: string): string | null {
  if (args === null || typeof args !== 'object') return null;
  const value = (args as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** Text z vnoreneho objektu `args` (napr. `customer.name` u navrhu faktury, NAP5). */
function objectArgText(args: unknown, key: string, nested: string): string | null {
  if (args === null || typeof args !== 'object') return null;
  const value = (args as Record<string, unknown>)[key];
  return argText(value, nested);
}

export function approvalSentence(item: Pick<McpApprovalItem, 'tool' | 'args'>): string {
  const title = argText(item.args, 'title');
  switch (item.tool) {
    case 'reos.cases.create':
      return title !== null
        ? t('webAdmin.approvals.sentence.casesCreate', { locale: LOCALE, values: { title } })
        : t('webAdmin.approvals.sentence.casesCreateNoTitle', { locale: LOCALE });
    case 'reos.tasks.create':
      return title !== null
        ? t('webAdmin.approvals.sentence.tasksCreate', { locale: LOCALE, values: { title } })
        : t('webAdmin.approvals.sentence.tasksCreateNoTitle', { locale: LOCALE });
    case 'reos.calendar.create':
      return title !== null
        ? t('webAdmin.approvals.sentence.calendarCreate', { locale: LOCALE, values: { title } })
        : t('webAdmin.approvals.sentence.calendarCreateNoTitle', { locale: LOCALE });
    case 'reos.inquiries.create':
      return title !== null
        ? t('webAdmin.approvals.sentence.inquiriesCreate', { locale: LOCALE, values: { title } })
        : t('webAdmin.approvals.sentence.inquiriesCreateNoTitle', { locale: LOCALE });
    case 'reos.cases.add_note':
      return t('webAdmin.approvals.sentence.casesAddNote', { locale: LOCALE });
    /*
     * Krok NAP5 bod A12 — dva nove zapisove nastroje. Faktura nema `title`:
     * veta se sklada z odberatele (`customer.name`), stejne jako ho vidi
     * makler na faktuře. Smlouva `title` ma.
     */
    case 'reos.invoices.draft': {
      const customer = objectArgText(item.args, 'customer', 'name');
      return customer !== null
        ? t('webAdmin.approvals.sentence.invoicesDraft', { locale: LOCALE, values: { title: customer } })
        : t('webAdmin.approvals.sentence.invoicesDraftNoTitle', { locale: LOCALE });
    }
    case 'reos.contracts.prepare':
      return title !== null
        ? t('webAdmin.approvals.sentence.contractsPrepare', { locale: LOCALE, values: { title } })
        : t('webAdmin.approvals.sentence.contractsPrepareNoTitle', { locale: LOCALE });
    default:
      // Neznamy (budouci) nastroj vypise sve strojove jmeno, ne spadne —
      // vzor `kindLabel` v correspondence-queue.tsx.
      return t('webAdmin.approvals.sentence.generic', { locale: LOCALE, values: { tool: item.tool } });
  }
}
